import { Box, Typography, styled } from '@mui/material'

import { IProductConfiguration } from '@/types'
import { Chip } from '@/components/UI/Chip'
import { Copy } from '@/components/UI/Copy'
import { ITableColumn } from '@/components/UI/DataTable/TableHead'
import { formatAmountRange, formatPercentage, formatText, formatTextRange } from './format'

// Rows are grouped under their product name, so within a section the version is
// what distinguishes one row from the next and what they sort on by default.
export const DEFAULT_SORT_BY = 'version'

// Widths are fixed percentages summing to 100 so the table is bounded by the
// panel instead of growing to fit its widest value — a long description or a
// UUID used to push the whole page into a horizontal scroll.
//
// Product name is not a column: it is the section heading above each group, the
// same way a rule's category is.
export const productColumns: ITableColumn<IProductConfiguration>[] = [
  {
    key: 'productKey',
    id: 'productKey',
    width: '18%',
    label: 'Product Key',
    enableSorting: true,
    render: (value: unknown) => <StyledText>{formatText(value)}</StyledText>,
  },
  {
    key: 'productStatus',
    id: 'productStatus',
    width: '11%',
    label: 'Status',
    enableSorting: true,
    render: (value: unknown) =>
      value ? <Chip value={String(value)} /> : <StyledText>{formatText(value)}</StyledText>,
  },
  {
    key: 'version',
    id: 'version',
    width: '10%',
    label: 'Version',
    enableSorting: true,
    render: (value: unknown) => <StrongText>{formatText(value)}</StrongText>,
  },
  {
    key: 'minLimit',
    id: 'minLimit',
    width: '16%',
    label: 'Limits',
    align: 'right',
    // Sorts on the lower bound, which is the value the column is keyed on.
    enableSorting: true,
    render: (_value: unknown, row: IProductConfiguration) => (
      <StyledText>{formatAmountRange(row.minLimit, row.maxLimit)}</StyledText>
    ),
  },
  {
    key: 'minTerm',
    id: 'minTerm',
    width: '14%',
    label: 'Term',
    render: (_value: unknown, row: IProductConfiguration) => (
      <StyledText>{formatTextRange(row.minTerm, row.maxTerm)}</StyledText>
    ),
  },
  {
    key: 'interestRate',
    id: 'interestRate',
    width: '12%',
    label: 'Interest Rate',
    enableSorting: true,
    render: (value: unknown) => <StyledText>{formatPercentage(value)}</StyledText>,
  },
  {
    key: 'id',
    id: 'id',
    width: '19%',
    // Not sortable: ordering opaque UUIDs tells an agent nothing.
    label: 'Product ID',
    // Wrapped so the copy click does not bubble to the row handler, which would
    // collapse the row the agent is reading.
    render: (value: unknown) =>
      value ? (
        <Box
          onClick={event => event.stopPropagation()}
          sx={{ display: 'inline-flex', alignItems: 'center' }}
        >
          <Copy value={String(value)} textStyle={{ fontSize: '14px', color: '#475467' }} />
        </Box>
      ) : (
        <StyledText>{formatText(value)}</StyledText>
      ),
  },
]

const StyledText = styled(Typography)({
  fontSize: '14px',
  fontFamily: 'Inter',
  color: '#475467',
  wordBreak: 'break-word',
})

const StrongText = styled(StyledText)({
  fontWeight: 600,
  color: '#101828',
})
